"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";
import { Tooltip } from "./Tooltip";

// d2d-XDR eval 4.5 — one Timestamp. Times were formatted page by page
// (toLocaleString here, a relative "5m ago" there, raw ISO elsewhere), so the
// same instant read three ways across one investigation, and an analyst
// matching a row against a UTC log line had to convert in their head.
//
//   <Timestamp value={row.created_at} />                 absolute, in the zone preference
//   <Timestamp value={row.created_at} format="relative" /> "5 min ago", live
//   <Timestamp value={row.created_at} format="both" />     absolute · relative
//
// The zone is ONE preference for the whole console (Local or UTC), stored per
// browser and broadcast, so flipping it anywhere — <TimeZoneToggle>, or the
// TimeRangePicker's exact-window toggle — re-renders every timestamp. The
// tooltip always carries the other zone and the relative form, and the
// element is a real <time dateTime> for assistive tech and copy-paste.

export type TimeZonePreference = "local" | "utc";
export type TimestampFormat = "absolute" | "relative" | "both";

const STORAGE_KEY = "d2d-time-zone";
const EVENT = "d2d-time-zone-change";

function readPreference(): TimeZonePreference {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "utc" ? "utc" : "local";
  } catch {
    return "local";
  }
}

export function setTimeZonePreference(next: TimeZonePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* blocked storage: the choice lasts for this page only */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
}

/** The console-wide Local/UTC preference, live across components and tabs. */
export function useTimeZonePreference(): [TimeZonePreference, (next: TimeZonePreference) => void] {
  // "local" on the server and the first client render, so hydration matches;
  // the stored preference is applied right after mount.
  const [zone, setZone] = useState<TimeZonePreference>("local");
  useEffect(() => {
    setZone(readPreference());
    const onChange = () => setZone(readPreference());
    const onStorage = (e: StorageEvent) => { if (e.key === STORAGE_KEY) onChange(); };
    window.addEventListener(EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  return [zone, setTimeZonePreference];
}

/* One shared 30s tick for every relative timestamp on the page, rather than a
   timer per row. */
const listeners = new Set<() => void>();
let ticker: ReturnType<typeof setInterval> | null = null;
function useMinuteTick(enabled: boolean): void {
  const [, force] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const fn = () => force((n) => n + 1);
    listeners.add(fn);
    if (!ticker) ticker = setInterval(() => listeners.forEach((l) => l()), 30_000);
    return () => {
      listeners.delete(fn);
      if (listeners.size === 0 && ticker) { clearInterval(ticker); ticker = null; }
    };
  }, [enabled]);
}

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatAbsolute(d: Date, zone: TimeZonePreference, withSeconds = true): string {
  const text = new Intl.DateTimeFormat(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", ...(withSeconds ? { second: "2-digit" } : {}),
    hour12: false,
    timeZone: zone === "utc" ? "UTC" : undefined,
  }).format(d);
  return zone === "utc" ? `${text} UTC` : text;
}

export function formatRelative(d: Date, now: number = Date.now()): string {
  const diff = (d.getTime() - now) / 1000;
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto", style: "short" });
  if (abs < 45) return rtf.format(Math.round(diff), "second");
  if (abs < 2700) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 79200) return rtf.format(Math.round(diff / 3600), "hour");
  if (abs < 2592000) return rtf.format(Math.round(diff / 86400), "day");
  if (abs < 31536000) return rtf.format(Math.round(diff / 2592000), "month");
  return rtf.format(Math.round(diff / 31536000), "year");
}

export interface TimestampProps {
  value: string | number | Date | null | undefined;
  format?: TimestampFormat;
  /** Drop seconds from the absolute form. */
  noSeconds?: boolean;
  /** Shown when the value is missing or unparseable. */
  fallback?: string;
  className?: string;
}

export function Timestamp({ value, format = "absolute", noSeconds, fallback = "—", className }: TimestampProps) {
  const [preference] = useTimeZonePreference();
  // d2d-DetectHub eval: "local" means the server's zone during SSR and the
  // reader's after hydration, so the text could change under the reader (and
  // mismatch hydration). Until mounted, render the one zone both agree on.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const zone: TimeZonePreference = mounted ? preference : "utc";
  useMinuteTick(format !== "absolute");
  const d = toDate(value);
  if (!d) return <span className={cn("rc-timestamp rc-timestamp--empty", className)}>{fallback}</span>;

  const absolute = formatAbsolute(d, zone, !noSeconds);
  const relative = formatRelative(d);
  const other = formatAbsolute(d, zone === "utc" ? "local" : "utc");
  // "relative" depends on the render's clock too, so it waits for mount as well.
  const text = !mounted ? absolute : format === "relative" ? relative : format === "both" ? `${absolute} · ${relative}` : absolute;
  const detail = format === "relative" ? `${absolute} (${other})` : `${relative} · ${other}`;

  return (
    <Tooltip content={detail}>
      <time dateTime={d.toISOString()} className={cn("rc-timestamp", className)}>
        {text}
      </time>
    </Tooltip>
  );
}

/** A compact Local/UTC switch for the zone preference, for toolbars. */
export function TimeZoneToggle({ className }: { className?: string }) {
  const [zone, setZone] = useTimeZonePreference();
  return (
    <div className={cn("rc-tztoggle", className)} role="group" aria-label="Time zone for timestamps">
      {(["local", "utc"] as const).map((z) => (
        <button key={z} type="button" className="rc-tztoggle__opt" aria-pressed={zone === z} onClick={() => setZone(z)}>
          {z === "local" ? "Local" : "UTC"}
        </button>
      ))}
    </div>
  );
}
